import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, MoreHorizontal, Edit, Trash2, Loader2, X, AlertTriangle, Warehouse, Truck, Box, History, ArrowRightLeft, RotateCcw, ArrowDown, ArrowUp, Package } from 'lucide-react';

const tipoLabels = { entrada:'Entrada', asignacion:'Asignación', devolucion:'Devolución', consumo:'Consumo', 'daño':'Daño', perdida:'Pérdida', reposicion_consumo:'Repos. Consumo', reposicion_entrega:'Repos. Entrega' };

export default function Stock() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [summary, setSummary] = useState({});
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('tienda');
  const [techFilter, setTechFilter] = useState('all');
  const [techStock, setTechStock] = useState([]);
  const [saving, setSaving] = useState(false);

  // Dialogs
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [repoDialogOpen, setRepoDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ en_tienda: 0, consumido: 0, dañado: 0, perdido: 0, motivo: '', assignments: [] });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ technician_id: '', technician_name: '', observaciones: '' });
  const [bulkSearchName, setBulkSearchName] = useState('');
  const [bulkSearchClave, setBulkSearchClave] = useState('');
  const [bulkCatFilter, setBulkCatFilter] = useState('all');
  const [bulkSelected, setBulkSelected] = useState({}); // {article_id: cantidad}
  const [selectedArticle, setSelectedArticle] = useState(null);

  // Forms
  const [articleForm, setArticleForm] = useState({ nombre: '', categoria: 'material', descripcion: '', stock_minimo: 0, cantidad_inicial: 0, serie: '', clave_interna: '' });
  const [entryForm, setEntryForm] = useState({ article_id: '', cantidad: 1, observaciones: '' });
  const [assignForm, setAssignForm] = useState({ article_id: '', technician_id: '', technician_name: '', cantidad: 1, observaciones: '' });
  const [returnForm, setReturnForm] = useState({ article_id: '', technician_id: '', cantidad: 1, observaciones: '' });
  const [actionForm, setActionForm] = useState({ article_id: '', technician_id: '', action: 'consumo', cantidad: 1, observaciones: '' });
  const [repoForm, setRepoForm] = useState({ article_id: '', technician_id: '', technician_name: '', cantidad_consumir: 0, cantidad_nueva: 0, observaciones: '' });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (catFilter !== 'all') params.append('categoria', catFilter);
      const [artRes, techRes, sumRes, movRes] = await Promise.all([
        axios.get(`${API}/stock/articles?${params}`),
        axios.get(`${API}/technicians`),
        axios.get(`${API}/stock/summary`),
        axios.get(`${API}/stock/movements?limit=100`)
      ]);
      setArticles(artRes.data);
      setTechnicians(techRes.data.filter(t => t.is_active !== false));
      setSummary(sumRes.data);
      setMovements(movRes.data);
    } catch { toast.error('Error al cargar inventario'); }
    finally { setLoading(false); }
  }, [search, catFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchTechStock = async (techId) => {
    if (!techId || techId === 'all') { setTechStock([]); return; }
    try { const res = await axios.get(`${API}/stock/by-technician/${techId}`); setTechStock(res.data); }
    catch { setTechStock([]); }
  };
  useEffect(() => { if (activeTab === 'tecnicos') fetchTechStock(techFilter); }, [techFilter, activeTab]);

  // --- Handlers ---
  const handleSaveArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.nombre) { toast.error('Nombre obligatorio'); return; }
    setSaving(true);
    try {
      if (selectedArticle) {
        await axios.put(`${API}/stock/articles/${selectedArticle.id}`, articleForm);
        toast.success('Artículo actualizado');
      } else {
        const res = await axios.post(`${API}/stock/articles`, { ...articleForm });
        if (articleForm.cantidad_inicial > 0) {
          await axios.post(`${API}/stock/entry`, { article_id: res.data.id, cantidad: articleForm.cantidad_inicial, observaciones: 'Cantidad inicial' });
        }
        toast.success('Artículo creado');
      }
      setArticleDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleEntry = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await axios.post(`${API}/stock/entry`, entryForm); toast.success('Entrada registrada'); setEntryDialogOpen(false); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await axios.post(`${API}/stock/assign`, assignForm); toast.success('Asignación exitosa'); setAssignDialogOpen(false); fetchData(); if (activeTab === 'tecnicos') fetchTechStock(techFilter); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleReturn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await axios.post(`${API}/stock/return`, returnForm); toast.success('Devolución registrada'); setReturnDialogOpen(false); fetchData(); fetchTechStock(techFilter); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleAction = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await axios.post(`${API}/stock/tech-action`, actionForm); toast.success('Registrado'); setActionDialogOpen(false); fetchData(); fetchTechStock(techFilter); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleReposition = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await axios.post(`${API}/stock/reposition`, repoForm); toast.success('Reposición completada'); setRepoDialogOpen(false); fetchData(); fetchTechStock(techFilter); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDeleteArticle = async (art) => {
    if (!window.confirm(`¿Eliminar "${art.nombre}" y todo su historial?`)) return;
    try { await axios.delete(`${API}/stock/articles/${art.id}`); toast.success('Eliminado'); fetchData(); }
    catch { toast.error('Error'); }
  };

  // Open helpers
  const openAssignFromArticle = (art) => {
    setAssignForm({ article_id: art.id, technician_id: '', technician_name: '', cantidad: 1, observaciones: '' });
    setSelectedArticle(art);
    setAssignDialogOpen(true);
  };

  const openReturnFromTech = (item) => {
    setReturnForm({ article_id: item.article_id, technician_id: item.technician_id, cantidad: 1, observaciones: '' });
    setReturnDialogOpen(true);
  };

  const openActionFromTech = (item, action) => {
    setActionForm({ article_id: item.article_id, technician_id: item.technician_id, action, cantidad: 1, observaciones: '' });
    setActionDialogOpen(true);
  };

  const openRepoFromTech = (item) => {
    setRepoForm({ article_id: item.article_id, technician_id: item.technician_id, technician_name: item.technician_name, cantidad_consumir: item.cantidad, cantidad_nueva: item.cantidad, observaciones: '' });
    setRepoDialogOpen(true);
  };

  const openAdjust = (art) => {
    setSelectedArticle(art);
    setAdjustForm({
      en_tienda: art.en_tienda || 0,
      consumido: art.consumido || 0,
      dañado: art.dañado || 0,
      perdido: art.perdido || 0,
      motivo: '',
      assignments: (art.assignments || []).map(a => ({ technician_id: a.technician_id, technician_name: a.technician_name, cantidad: a.cantidad }))
    });
    setAdjustDialogOpen(true);
  };

  const updateAdjustAssign = (techId, cantidad) => {
    setAdjustForm(prev => {
      const list = [...(prev.assignments || [])];
      const idx = list.findIndex(a => a.technician_id === techId);
      const tech = technicians.find(t => t.id === techId);
      const qty = Math.max(0, parseInt(cantidad) || 0);
      if (idx >= 0) {
        list[idx] = { ...list[idx], cantidad: qty };
      } else if (tech) {
        list.push({ technician_id: techId, technician_name: tech.name, cantidad: qty });
      }
      return { ...prev, assignments: list };
    });
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!selectedArticle) return;
    setSaving(true);
    try {
      const payload = {
        en_tienda: parseInt(adjustForm.en_tienda) || 0,
        consumido: parseInt(adjustForm.consumido) || 0,
        'dañado': parseInt(adjustForm.dañado) || 0,
        perdido: parseInt(adjustForm.perdido) || 0,
        motivo: adjustForm.motivo,
        assignments: (adjustForm.assignments || []).filter(a => a.cantidad > 0)
      };
      await axios.post(`${API}/stock/articles/${selectedArticle.id}/adjust`, payload);
      toast.success('Stock ajustado');
      setAdjustDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ajustar');
    } finally { setSaving(false); }
  };

  // Asignación múltiple helpers
  const openBulkAssign = () => {
    setBulkForm({ technician_id: '', technician_name: '', observaciones: '' });
    setBulkSelected({});
    setBulkSearchName('');
    setBulkSearchClave('');
    setBulkCatFilter('all');
    setBulkDialogOpen(true);
  };

  const toggleBulkArticle = (art, checked) => {
    setBulkSelected(prev => {
      const copy = { ...prev };
      if (checked) copy[art.id] = copy[art.id] || 1;
      else delete copy[art.id];
      return copy;
    });
  };

  const setBulkQty = (artId, qty) => {
    setBulkSelected(prev => ({ ...prev, [artId]: Math.max(0, parseInt(qty) || 0) }));
  };

  const handleBulkSubmit = async () => {
    if (!bulkForm.technician_id) { toast.error('Selecciona un técnico'); return; }
    const items = Object.entries(bulkSelected).filter(([_, q]) => q > 0).map(([article_id, cantidad]) => ({ article_id, cantidad }));
    if (items.length === 0) { toast.error('Selecciona al menos un material'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stock/assign-bulk`, {
        technician_id: bulkForm.technician_id,
        technician_name: bulkForm.technician_name,
        items,
        observaciones: bulkForm.observaciones
      });
      const totalQty = items.reduce((s, it) => s + it.cantidad, 0);
      toast.success(`${items.length} artículo(s) (${totalQty} pz) asignado(s) a ${bulkForm.technician_name}`);
      setBulkDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error en asignación');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Inventario Local</h2>
          <p className="text-muted-foreground text-sm">Control por cantidades — materiales y herramientas</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setEntryForm({ article_id: '', cantidad: 1, observaciones: '' }); setEntryDialogOpen(true); }} data-testid="entry-btn">
            <ArrowDown className="h-4 w-4 mr-1" />Entrada
          </Button>
          <Button variant="outline" size="sm" onClick={openBulkAssign} data-testid="bulk-assign-btn">
            <Truck className="h-4 w-4 mr-1" />Asignación múltiple
          </Button>
          {user?.role === 'admin' && (
            <Button variant="outline" size="sm" className="text-destructive border-destructive/50 hover:bg-destructive/10" onClick={async () => {
              if (!window.confirm('¿Estás seguro de BORRAR TODOS los artículos del inventario local? Esta acción no se puede deshacer.')) return;
              try { const r = await axios.delete(`${API}/stock/articles-all`); toast.success(r.data.message); fetchData(); }
              catch { toast.error('Error al borrar'); }
            }} data-testid="delete-all-btn">
              <Trash2 className="h-4 w-4 mr-1" />Borrar todo
            </Button>
          )}
          <Button onClick={() => { setSelectedArticle(null); setArticleForm({ nombre: '', categoria: 'material', descripcion: '', stock_minimo: 0, cantidad_inicial: 0, serie: '', clave_interna: '' }); setArticleDialogOpen(true); }} data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-1" />Nuevo Artículo
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
        {[
          { label: 'Total', val: summary.total||0, dot: 'bg-slate-500' },
          { label: 'Tienda', val: summary.en_tienda||0, dot: 'bg-emerald-500' },
          { label: 'Técnicos', val: summary.con_tecnicos||0, dot: 'bg-blue-500' },
          { label: 'Disponible', val: summary.disponible||0, dot: 'bg-green-500' },
          { label: 'Materiales', val: summary.materiales||0, dot: 'bg-orange-500' },
          { label: 'Herram.', val: summary.herramientas||0, dot: 'bg-purple-500' },
          { label: 'Consumido', val: summary.consumido||0, dot: 'bg-gray-400' },
          { label: 'Dañado', val: summary.dañado||0, dot: 'bg-red-500' },
          { label: 'Perdido', val: summary.perdido||0, dot: 'bg-red-700' },
        ].map(c => (
          <Card key={c.label} className="hover:shadow-sm transition-shadow">
            <CardContent className="p-2.5 text-center">
              <div className="flex items-center justify-center gap-1.5 mb-1"><div className={`w-2 h-2 rounded-full ${c.dot}`} /><span className="text-xs text-muted-foreground">{c.label}</span></div>
              <span className="text-xl font-bold">{c.val}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tienda"><Warehouse className="h-4 w-4 mr-1.5" />Tienda</TabsTrigger>
          <TabsTrigger value="tecnicos"><Truck className="h-4 w-4 mr-1.5" />Por Técnico</TabsTrigger>
          <TabsTrigger value="consolidado"><Box className="h-4 w-4 mr-1.5" />Consolidado</TabsTrigger>
          <TabsTrigger value="movimientos"><History className="h-4 w-4 mr-1.5" />Movimientos</TabsTrigger>
        </TabsList>

        {/* TIENDA */}
        <TabsContent value="tienda" className="space-y-3 mt-3">
          <div className="flex gap-3">
            <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Buscar por nombre, clave o serie..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" /></div>
            <Select value={catFilter} onValueChange={setCatFilter}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Todas</SelectItem><SelectItem value="material">Materiales</SelectItem><SelectItem value="herramienta">Herramientas</SelectItem></SelectContent></Select>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Artículo</TableHead>
                <TableHead className="w-[90px]">Clave</TableHead>
                <TableHead className="w-[70px]">Cat.</TableHead>
                <TableHead className="w-[100px]">Serie</TableHead>
                <TableHead className="w-[60px] text-center">Total</TableHead>
                <TableHead className="w-[70px] text-center">Tienda</TableHead>
                <TableHead className="w-[80px] text-center">Técnicos</TableHead>
                <TableHead className="w-[70px] text-center">Consum.</TableHead>
                <TableHead className="w-[60px] text-center">Dañ.</TableHead>
                <TableHead className="w-[60px] text-center">Perd.</TableHead>
                <TableHead className="w-[60px]">Acc.</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {articles.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No hay artículos</TableCell></TableRow>
                ) : articles.map(art => (
                  <TableRow key={art.id} className={art.stock_minimo > 0 && art.en_tienda < art.stock_minimo ? 'bg-red-50/50 dark:bg-red-950/10' : ''}>
                    <TableCell>
                      <span className="font-medium text-sm">{art.nombre}</span>
                      {art.stock_minimo > 0 && art.en_tienda < art.stock_minimo && (
                        <Badge variant="outline" className="ml-2 text-xs text-red-600 border-red-300"><AlertTriangle className="h-3 w-3 mr-1" />Stock bajo (mín: {art.stock_minimo})</Badge>
                      )}
                      {art.assignments?.length > 0 && (
                        <div className="flex gap-1 mt-0.5 flex-wrap">{art.assignments.map((a,i) => (
                          <span key={i} className="text-xs text-blue-600 dark:text-blue-400">{a.technician_name?.split(' ')[0]}:{a.cantidad}</span>
                        ))}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs font-bold text-blue-600">{art.clave_interna || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{art.categoria === 'material' ? 'Mat.' : 'Herr.'}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{art.serie || '-'}</TableCell>
                    <TableCell className="text-center font-bold">{art.cantidad_total}</TableCell>
                    <TableCell className="text-center font-medium text-green-600">{art.en_tienda}</TableCell>
                    <TableCell className="text-center font-medium text-blue-600">{art.con_tecnicos}</TableCell>
                    <TableCell className="text-center text-gray-500">{art.consumido || 0}</TableCell>
                    <TableCell className="text-center text-red-500">{art.dañado || 0}</TableCell>
                    <TableCell className="text-center text-red-700">{art.perdido || 0}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openAssignFromArticle(art)}><ArrowRightLeft className="h-4 w-4 mr-2" />Asignar a técnico</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setEntryForm({ article_id: art.id, cantidad: 1, observaciones: '' }); setEntryDialogOpen(true); }}><ArrowDown className="h-4 w-4 mr-2" />Entrada de stock</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelectedArticle(art); setArticleForm({ nombre: art.nombre, categoria: art.categoria, descripcion: art.descripcion||'', stock_minimo: art.stock_minimo||0, serie: art.serie||'', clave_interna: art.clave_interna||'', cantidad_inicial: 0 }); setArticleDialogOpen(true); }}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => openAdjust(art)} data-testid={`adjust-stock-${art.id}`}><RotateCcw className="h-4 w-4 mr-2" />Ajustar stock</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteArticle(art)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* POR TÉCNICO */}
        <TabsContent value="tecnicos" className="space-y-3 mt-3">
          <div className="flex gap-2 flex-wrap">
            {technicians.map(t => (
              <Button key={t.id} variant={techFilter === t.id ? 'default' : 'outline'} size="sm" onClick={() => { setTechFilter(t.id); }}>{t.name.split(' ')[0]}</Button>
            ))}
          </div>
          {techFilter !== 'all' ? (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Artículo</TableHead>
                  <TableHead className="w-[70px]">Cat.</TableHead>
                  <TableHead className="w-[80px] text-center">Cantidad</TableHead>
                  <TableHead className="w-[60px]">Acc.</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {techStock.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Sin material asignado</TableCell></TableRow>
                  ) : techStock.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium text-sm">{item.article_nombre}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{item.categoria === 'material' ? 'Mat.' : 'Herr.'}</Badge></TableCell>
                      <TableCell className="text-center font-bold text-lg">{item.cantidad}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openReturnFromTech(item)}><RotateCcw className="h-4 w-4 mr-2" />Devolver a tienda</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openActionFromTech(item, 'consumo')}>Registrar consumo</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openActionFromTech(item, 'daño')} className="text-orange-600">Registrar daño</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openActionFromTech(item, 'perdida')} className="text-red-600">Registrar pérdida</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => openRepoFromTech(item)} className="text-blue-600"><ArrowRightLeft className="h-4 w-4 mr-2" />Reponer material</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          ) : <p className="text-muted-foreground text-center py-8">Selecciona un técnico</p>}
        </TabsContent>

        {/* CONSOLIDADO */}
        <TabsContent value="consolidado" className="space-y-3 mt-3">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Artículo</TableHead><TableHead className="w-[70px]">Cat.</TableHead>
                <TableHead className="w-[60px] text-center">Total</TableHead><TableHead className="w-[70px] text-center">Tienda</TableHead>
                <TableHead className="w-[80px] text-center">Técnicos</TableHead><TableHead className="w-[70px] text-center">Consum.</TableHead>
                <TableHead className="w-[60px] text-center">Dañ.</TableHead><TableHead className="w-[60px] text-center">Perd.</TableHead>
              </TableRow></TableHeader>
              <TableBody>{articles.map(art => (
                <TableRow key={art.id}>
                  <TableCell className="font-medium">{art.nombre}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{art.categoria === 'material' ? 'Mat.' : 'Herr.'}</Badge></TableCell>
                  <TableCell className="text-center font-bold">{art.cantidad_total}</TableCell>
                  <TableCell className="text-center text-green-600">{art.en_tienda}</TableCell>
                  <TableCell className="text-center text-blue-600">{art.con_tecnicos}</TableCell>
                  <TableCell className="text-center text-gray-500">{art.consumido||0}</TableCell>
                  <TableCell className="text-center text-red-500">{art.dañado||0}</TableCell>
                  <TableCell className="text-center text-red-700">{art.perdido||0}</TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* MOVIMIENTOS */}
        <TabsContent value="movimientos" className="space-y-3 mt-3">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-[130px]">Fecha</TableHead><TableHead>Artículo</TableHead>
                <TableHead className="w-[110px]">Tipo</TableHead><TableHead className="w-[70px] text-center">Cant.</TableHead>
                <TableHead className="w-[120px]">Técnico</TableHead><TableHead className="w-[100px]">Por</TableHead>
                <TableHead>Nota</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin movimientos</TableCell></TableRow>
                ) : movements.map(m => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs">{m.fecha?.slice(0,16).replace('T',' ')}</TableCell>
                    <TableCell className="text-sm font-medium">{m.article_nombre}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{tipoLabels[m.tipo]||m.tipo}</Badge></TableCell>
                    <TableCell className="text-center font-bold">{m.cantidad}</TableCell>
                    <TableCell className="text-xs">{m.technician_name||'-'}</TableCell>
                    <TableCell className="text-xs">{m.realizado_por}</TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-[120px]">{m.observaciones||'-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* === DIALOGS === */}

      {/* Article */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{selectedArticle ? 'Editar' : 'Nuevo'} Artículo</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveArticle} className="space-y-4">
            <div className="space-y-2"><Label>Nombre *</Label><Input value={articleForm.nombre} onChange={(e) => setArticleForm({...articleForm, nombre: e.target.value})} required data-testid="article-name" /></div>
            <div className="space-y-2"><Label>Clave Interna</Label><Input value={articleForm.clave_interna} onChange={(e) => setArticleForm({...articleForm, clave_interna: e.target.value})} placeholder="Se genera automáticamente si se deja vacío" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Categoría</Label>
                <Select value={articleForm.categoria} onValueChange={(v) => setArticleForm({...articleForm, categoria: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="material">Material</SelectItem><SelectItem value="herramienta">Herramienta</SelectItem></SelectContent></Select></div>
              <div className="space-y-2"><Label>Stock mínimo</Label><Input type="number" value={articleForm.stock_minimo} onChange={(e) => setArticleForm({...articleForm, stock_minimo: parseInt(e.target.value)||0})} min="0" /></div>
            </div>
            <div className="space-y-2"><Label>Serie del artículo</Label><Input value={articleForm.serie} onChange={(e) => setArticleForm({...articleForm, serie: e.target.value})} placeholder="Número de serie o código" /></div>
            {!selectedArticle && (
              <div className="space-y-2"><Label>Cantidad inicial en tienda</Label><Input type="number" value={articleForm.cantidad_inicial} onChange={(e) => setArticleForm({...articleForm, cantidad_inicial: parseInt(e.target.value)||0})} min="0" placeholder="0" /></div>
            )}
            <div className="space-y-2"><Label>Observaciones</Label><Input value={articleForm.descripcion} onChange={(e) => setArticleForm({...articleForm, descripcion: e.target.value})} placeholder="Notas opcionales" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setArticleDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Entry */}
      <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Entrada de Inventario</DialogTitle><DialogDescription>Agregar cantidad a tienda</DialogDescription></DialogHeader>
          <form onSubmit={handleEntry} className="space-y-4">
            <div className="space-y-2"><Label>Artículo *</Label>
              <Select value={entryForm.article_id} onValueChange={(v) => setEntryForm({...entryForm, article_id: v})}><SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger><SelectContent>{articles.map(a => <SelectItem key={a.id} value={a.id}>{a.nombre} (Tienda: {a.en_tienda})</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Cantidad</Label><Input type="number" value={entryForm.cantidad} onChange={(e) => setEntryForm({...entryForm, cantidad: parseInt(e.target.value)||1})} min="1" /></div>
            <div className="space-y-2"><Label>Observaciones</Label><Input value={entryForm.observaciones} onChange={(e) => setEntryForm({...entryForm, observaciones: e.target.value})} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEntryDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar Entrada'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Asignar a Técnico</DialogTitle><DialogDescription>{selectedArticle?.nombre} — Disponible en tienda: {selectedArticle?.en_tienda}</DialogDescription></DialogHeader>
          <form onSubmit={handleAssign} className="space-y-4">
            <div className="space-y-2"><Label>Técnico *</Label>
              <Select value={assignForm.technician_id} onValueChange={(v) => { const t = technicians.find(x=>x.id===v); setAssignForm({...assignForm, technician_id: v, technician_name: t?.name||''}); }}><SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger><SelectContent>{technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Cantidad (máx: {selectedArticle?.en_tienda})</Label><Input type="number" value={assignForm.cantidad} onChange={(e) => setAssignForm({...assignForm, cantidad: parseInt(e.target.value)||1})} min="1" max={selectedArticle?.en_tienda||999} /></div>
            <div className="space-y-2"><Label>Nota</Label><Input value={assignForm.observaciones} onChange={(e) => setAssignForm({...assignForm, observaciones: e.target.value})} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Asignar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Return */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Devolver a Tienda</DialogTitle></DialogHeader>
          <form onSubmit={handleReturn} className="space-y-4">
            <div className="space-y-2"><Label>Cantidad a devolver</Label><Input type="number" value={returnForm.cantidad} onChange={(e) => setReturnForm({...returnForm, cantidad: parseInt(e.target.value)||1})} min="1" /></div>
            <div className="space-y-2"><Label>Nota</Label><Input value={returnForm.observaciones} onChange={(e) => setReturnForm({...returnForm, observaciones: e.target.value})} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Devolver'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action (consumo/daño/perdida) */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Registrar {actionForm.action === 'consumo' ? 'Consumo' : actionForm.action === 'daño' ? 'Daño' : 'Pérdida'}</DialogTitle></DialogHeader>
          <form onSubmit={handleAction} className="space-y-4">
            <div className="space-y-2"><Label>Cantidad</Label><Input type="number" value={actionForm.cantidad} onChange={(e) => setActionForm({...actionForm, cantidad: parseInt(e.target.value)||1})} min="1" /></div>
            <div className="space-y-2"><Label>Nota</Label><Input value={actionForm.observaciones} onChange={(e) => setActionForm({...actionForm, observaciones: e.target.value})} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setActionDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} variant={actionForm.action === 'perdida' ? 'destructive' : 'default'}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reposition */}
      <Dialog open={repoDialogOpen} onOpenChange={setRepoDialogOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Reponer Material</DialogTitle><DialogDescription>Consumir saldo actual y entregar nueva cantidad</DialogDescription></DialogHeader>
          <form onSubmit={handleReposition} className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 text-sm space-y-1">
              <p><strong>Paso 1:</strong> Se consumirán <strong>{repoForm.cantidad_consumir}</strong> del técnico</p>
              <p><strong>Paso 2:</strong> Se entregarán <strong>{repoForm.cantidad_nueva}</strong> nuevos desde tienda</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Consumir (actual)</Label><Input type="number" value={repoForm.cantidad_consumir} onChange={(e) => setRepoForm({...repoForm, cantidad_consumir: parseInt(e.target.value)||0})} min="0" /></div>
              <div className="space-y-2"><Label>Entregar (nueva)</Label><Input type="number" value={repoForm.cantidad_nueva} onChange={(e) => setRepoForm({...repoForm, cantidad_nueva: parseInt(e.target.value)||0})} min="1" /></div>
            </div>
            <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={() => setRepoForm({...repoForm, cantidad_nueva: repoForm.cantidad_consumir})}>
              Reponer misma cantidad
            </Button>
            <div className="space-y-2"><Label>Nota</Label><Input value={repoForm.observaciones} onChange={(e) => setRepoForm({...repoForm, observaciones: e.target.value})} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setRepoDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reponer'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock (admin only) */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajustar stock — {selectedArticle?.nombre}</DialogTitle>
            <DialogDescription>Edita directamente las cantidades. Total = Tienda + Técnicos + Consumido + Dañado + Perdido.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjustSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">En tienda</Label><Input data-testid="adjust-tienda" type="number" min="0" value={adjustForm.en_tienda} onChange={(e) => setAdjustForm({...adjustForm, en_tienda: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Consumido</Label><Input type="number" min="0" value={adjustForm.consumido} onChange={(e) => setAdjustForm({...adjustForm, consumido: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Dañado</Label><Input type="number" min="0" value={adjustForm.dañado} onChange={(e) => setAdjustForm({...adjustForm, dañado: e.target.value})} /></div>
              <div className="space-y-1"><Label className="text-xs">Perdido</Label><Input type="number" min="0" value={adjustForm.perdido} onChange={(e) => setAdjustForm({...adjustForm, perdido: e.target.value})} /></div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Asignado a técnicos</Label>
              <div className="space-y-1 rounded-md border p-2 max-h-48 overflow-y-auto">
                {technicians.length === 0 && <p className="text-xs text-muted-foreground">Sin técnicos activos</p>}
                {technicians.map(t => {
                  const current = (adjustForm.assignments || []).find(a => a.technician_id === t.id);
                  return (
                    <div key={t.id} className="flex items-center justify-between gap-2">
                      <span className="text-sm">{t.name}</span>
                      <Input type="number" min="0" className="w-20 h-8" value={current?.cantidad ?? 0} onChange={(e) => updateAdjustAssign(t.id, e.target.value)} />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1"><Label className="text-xs">Motivo del ajuste (opcional)</Label><Input value={adjustForm.motivo} onChange={(e) => setAdjustForm({...adjustForm, motivo: e.target.value})} placeholder="Ej: Inventario físico, corrección" /></div>

            <div className="rounded-md bg-muted/50 p-2 text-xs flex justify-between">
              <span>Total resultante</span>
              <span className="font-bold">{(parseInt(adjustForm.en_tienda)||0) + (adjustForm.assignments||[]).reduce((s,a) => s + (parseInt(a.cantidad)||0), 0) + (parseInt(adjustForm.consumido)||0) + (parseInt(adjustForm.dañado)||0) + (parseInt(adjustForm.perdido)||0)}</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="adjust-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar ajuste'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk assign — multiple materials to one technician */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Asignación múltiple</DialogTitle>
            <DialogDescription>Asigna varios materiales a un técnico en una sola operación. Se descuentan de tienda automáticamente.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Técnico *</Label>
                <select
                  className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm"
                  value={bulkForm.technician_id}
                  onChange={(e) => {
                    const t = technicians.find(tt => tt.id === e.target.value);
                    setBulkForm({ ...bulkForm, technician_id: e.target.value, technician_name: t?.name || '' });
                  }}
                  data-testid="bulk-tech-select"
                >
                  <option value="">Seleccionar técnico...</option>
                  {technicians.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nota (opcional)</Label>
                <Input value={bulkForm.observaciones} onChange={(e) => setBulkForm({...bulkForm, observaciones: e.target.value})} placeholder="Ej: salida del día" />
              </div>
            </div>

            <div className="rounded-md border bg-blue-50/40 dark:bg-blue-950/20 p-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">📌 Materiales seleccionados ({Object.values(bulkSelected).filter(v => v > 0).length})</Label>
                {Object.keys(bulkSelected).length > 0 && (
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={() => setBulkSelected({})}>Limpiar todos</Button>
                )}
              </div>
              {Object.keys(bulkSelected).length === 0 ? (
                <p className="text-xs text-muted-foreground py-3 text-center">Aún no has seleccionado materiales. Búscalos abajo y marca la casilla.</p>
              ) : (
                <div className="space-y-1 max-h-[28vh] overflow-y-auto pr-1">
                  {Object.entries(bulkSelected).map(([artId, qty]) => {
                    const art = articles.find(a => a.id === artId);
                    if (!art) return null;
                    const overstock = qty > (art.en_tienda || 0);
                    return (
                      <div key={artId} className="flex items-center gap-2 bg-background rounded border p-2 text-sm" data-testid={`selected-${artId}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{art.nombre}</p>
                          <p className="text-xs text-muted-foreground"><span className="font-mono text-blue-600">{art.clave_interna || '-'}</span> · Disponible: <span className="font-semibold text-emerald-600">{art.en_tienda || 0}</span></p>
                        </div>
                        <Input type="number" min="1" max={art.en_tienda || 0} className={`w-20 h-8 ${overstock ? 'border-red-500 text-red-600' : ''}`} value={qty} onChange={(e) => setBulkQty(artId, e.target.value)} />
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => toggleBulkArticle(art, false)} data-testid={`remove-selected-${artId}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="relative sm:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por nombre..." value={bulkSearchName} onChange={(e) => setBulkSearchName(e.target.value)} className="pl-9" data-testid="bulk-search-nombre" />
              </div>
              <div className="relative sm:col-span-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por clave interna..." value={bulkSearchClave} onChange={(e) => setBulkSearchClave(e.target.value)} className="pl-9" data-testid="bulk-search-clave" />
              </div>
              <Select value={bulkCatFilter} onValueChange={setBulkCatFilter}>
                <SelectTrigger data-testid="bulk-cat-filter"><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  <SelectItem value="material">Materiales</SelectItem>
                  <SelectItem value="herramienta">Herramientas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-md border max-h-[40vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                  <tr className="text-xs">
                    <th className="px-2 py-1 w-8"></th>
                    <th className="px-2 py-1 text-left">Artículo</th>
                    <th className="px-2 py-1 w-20 text-left">Clave</th>
                    <th className="px-2 py-1 w-14 text-left">Cat.</th>
                    <th className="px-2 py-1 w-14 text-center">Tienda</th>
                    <th className="px-2 py-1 w-24 text-center">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.filter(a => {
                    if (bulkCatFilter !== 'all' && a.categoria !== bulkCatFilter) return false;
                    if (bulkSearchName && !(a.nombre || '').toLowerCase().includes(bulkSearchName.toLowerCase())) return false;
                    if (bulkSearchClave && !(a.clave_interna || '').toLowerCase().includes(bulkSearchClave.toLowerCase())) return false;
                    return true;
                  }).map(art => {
                    const checked = bulkSelected[art.id] !== undefined;
                    const qty = bulkSelected[art.id] || 0;
                    const overstock = qty > (art.en_tienda || 0);
                    return (
                      <tr key={art.id} className={`border-t ${checked ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`}>
                        <td className="px-2 py-1.5 text-center">
                          <Checkbox checked={checked} onCheckedChange={(v) => toggleBulkArticle(art, !!v)} data-testid={`bulk-check-${art.id}`} />
                        </td>
                        <td className="px-2 py-1.5">{art.nombre}</td>
                        <td className="px-2 py-1.5 font-mono text-xs text-blue-600">{art.clave_interna || '-'}</td>
                        <td className="px-2 py-1.5 text-xs">{art.categoria === 'material' ? 'Mat.' : 'Herr.'}</td>
                        <td className="px-2 py-1.5 text-center text-emerald-600 font-medium">{art.en_tienda || 0}</td>
                        <td className="px-2 py-1.5 text-center">
                          {checked ? (
                            <Input type="number" min="1" max={art.en_tienda || 0} className={`w-20 h-7 mx-auto ${overstock ? 'border-red-500 text-red-600' : ''}`} value={qty} onChange={(e) => setBulkQty(art.id, e.target.value)} />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {articles.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm">Sin artículos</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <DialogFooter className="border-t pt-3 mt-2">
            <div className="flex-1 text-xs text-muted-foreground self-center">
              {Object.values(bulkSelected).filter(v => v > 0).length} material(es) · Total: {Object.values(bulkSelected).reduce((s, v) => s + (parseInt(v) || 0), 0)} pz
            </div>
            <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={handleBulkSubmit} disabled={saving} data-testid="bulk-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Truck className="h-4 w-4 mr-1" />}
              Asignar materiales seleccionados
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
